import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

function routeGuardLog(...args: unknown[]): void {
  if (import.meta.env.DEV) {
    console.debug('[route]', ...args);
  }
}

interface RouteContextValue {
  pathname: string;
  navigate: (path: string) => void;
}

const RouteContext = createContext<RouteContextValue | undefined>(undefined);

/**
 * Lightweight client-side router built on the History API.
 * Reused from the Teacher Dashboard pattern. Tracks the current pathname and
 * provides a `navigate` helper that pushes history entries and dispatches
 * popstate-compatible navigation.
 */
export function RouteProvider({ children }: { children: ReactNode }) {
  const [pathname, setPathname] = useState<string>(
    () => window.location.pathname,
  );

  useEffect(() => {
    const handlePopState = () => {
      setPathname(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path: string) => {
    if (path === window.location.pathname) {
      return;
    }
    routeGuardLog('navigate', path);
    window.history.pushState({}, '', path);
    setPathname(path);
    window.scrollTo(0, 0);
  };

  return (
    <RouteContext.Provider value={{ pathname, navigate }}>
      {children}
    </RouteContext.Provider>
  );
}

export function useRoute(): RouteContextValue {
  const ctx = useContext(RouteContext);
  if (!ctx) {
    throw new Error('useRoute must be used within a RouteProvider');
  }
  return ctx;
}
